import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";

import Menu from "antd/es/menu";
import Popover from "antd/es/popover";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import { SearchField } from "@mapmycustomers/ui";

import { displayNameComparator } from "@app/util/comparator";
import leaderboardFieldModel from "@app/util/fieldModel/LeaderboardFieldModel";

import styles from "./MetricDropdown.module.scss";

const includeAll = () => true;

interface Props {
  activeMetric?: IField;
  children: ReactNode;
  filterMetric?: (field: IField) => boolean;
  onSelect?: (field: IField) => void;
  onVisibilityChange?: (visible: boolean) => void;
}

const MetricDropdown: React.FC<Props> = ({
  activeMetric,
  children,
  filterMetric = includeAll,
  onSelect,
  onVisibilityChange,
}) => {
  const [filter, setFilter] = useState<string>("");

  // this is needed to prevent hiding dropdown when user clicks elsewhere - e.g. on the search field
  const [visible, setVisibility] = useState<boolean>(false);

  useEffect(() => {
    onVisibilityChange?.(visible);
  }, [visible, onVisibilityChange]);

  const handleVisibilityChange = useCallback((visible) => {
    setVisibility(visible);
    if (!visible) {
      setFilter(""); // cleanup filter when hiding
    }
  }, []);

  const metricFields = useMemo(() => {
    const query = filter.toLowerCase().trim();
    return leaderboardFieldModel.fields
      .filter((field) => field.hasFeature(FieldFeature.METRIC) && filterMetric(field))
      .filter((field) => !query || field.displayName.toLowerCase().includes(query))
      .sort(displayNameComparator);
  }, [filter, filterMetric]);

  const handleSelect = useCallback(
    ({ keyPath }) => {
      const fieldName = Array.isArray(keyPath) && keyPath.length === 1 ? keyPath[0] : undefined;
      if (!fieldName) {
        return;
      }
      const field = leaderboardFieldModel.getByName(fieldName);
      if (field && onSelect) {
        onSelect(field);
      }
      handleVisibilityChange(false);
    },
    [handleVisibilityChange, onSelect]
  );

  return (
    <Popover
      content={
        <div className={styles.overlay} onClick={stopEvents}>
          <SearchField
            className={styles.search}
            onChange={setFilter}
            placeholder="Search for ranking metric"
          />
          <div className={styles.menu}>
            <Menu
              defaultSelectedKeys={activeMetric ? [activeMetric.name] : undefined}
              onSelect={handleSelect}
            >
              {metricFields.map((field) => (
                <Menu.Item key={field.name}>{field.displayName}</Menu.Item>
              ))}
            </Menu>
          </div>
        </div>
      }
      destroyTooltipOnHide
      onOpenChange={handleVisibilityChange}
      open={visible}
      overlayClassName={styles.overlayContainer}
      placement="bottomLeft"
      title={null}
      trigger={["click"]}
    >
      {children}
    </Popover>
  );
};

export default MetricDropdown;
